import React, { useEffect } from 'react';
import Modal from './Modal';
import { ModalProps, ModalSize } from './Modal.types';
import {
  Button,
  ButtonType,
  IconButton,
  IconName,
  IconButtonSize,
  IconButtonVariant,
  Icon,
} from '@hills-design-system/components';
import { PetParentInfo, PetInfo, ValidationError } from '../../types/petParent';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import { ParsedUploadData } from './SpreadsheetUploadModal';
import Banner from '../Banner';
import { convertUTCToLocalDate } from '../../utils/dateUtils';

export interface ReviewPetParentModalProps extends Omit<ModalProps, 'children'> {
  /**
   * Report timeframe (e.g. "November 2025")
   */
  reportTimeframe?: string;
  /**
   * Array of parsed data from spreadsheet upload
   */
  parsedData?: ParsedUploadData[];
  /**
   * Function to call when the form is submitted
   */
  onSubmit: () => void;
  /**
   * Function to call when the user wants to edit a pet parent
   */
  onEdit: (petParentIndex: number) => void;
  /**
   * Function to call when the user wants to delete a pet parent
   */
  onDelete?: (petParentIndex: number) => void;
  /**
   * Function to call when the user wants to add more pet parents
   */
  onAddMore?: () => void;
  /**
   * Whether this is from a spreadsheet upload (affects UI behavior)
   */
  isFromSpreadsheet?: boolean;
  /**
   * Index of pet parent to scroll to (after editing)
   */
  scrollToPetParentIndex?: number | null;
  /**
   * Boolean state to indicate whether the form is submitting
   */
  isSubmitting?: boolean;
  /**
   * Whether this is for a Canadian shelter
   */
  isCanadian?: boolean;
}

/**
 * Modal for reviewing pet parent information before submission
 */
const ReviewPetParentModal: React.FC<ReviewPetParentModalProps> = ({
  title,
  eyebrow = 'Report Title',
  secondaryEyebrow,
  isOpen,
  onClose,
  reportTimeframe,
  parsedData = [],
  onSubmit,
  onEdit,
  onDelete,
  onAddMore,
  size = ModalSize.Large,
  showCloseButton = true,
  className = '',
  isFromSpreadsheet,
  scrollToPetParentIndex,
  isSubmitting = false,
  isCanadian = false,
}) => {
  const { t } = useTranslation();

  // Scroll to specific pet parent after editing
  useEffect(() => {
    if (isOpen && scrollToPetParentIndex !== null && scrollToPetParentIndex !== undefined) {
      // Small delay to ensure modal content is fully rendered
      setTimeout(() => {
        const petParentElement = document.querySelector(
          `[data-pet-parent-index="${scrollToPetParentIndex}"]`
        );
        if (petParentElement) {
          petParentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [isOpen, scrollToPetParentIndex]);

  // Calculate total errors across all pet parents
  const totalErrors = parsedData.reduce((total, data) => {
    return total + (data.validationErrors?.length ?? 0);
  }, 0);

  // Format address for display
  const formatAddress = (info: PetParentInfo) => {
    if (isCanadian) {
      return `${info.address}, ${info.city}, ${info.province} ${info.zip}`;
    }
    return `${info.address}, ${info.city}, ${info.state} ${info.zip}`;
  };

  // Format pet info for display
  const formatPetInfo = (pet: PetInfo) => {
    const species = pet.species ? pet.species : '';
    const name = pet.name ? pet.name : '';
    const birthdate = pet.birthdate ? calculateAge(pet.birthdate) : '';

    return `${name} • ${species} ${birthdate ? `• ${birthdate}` : ''}`;
  };

  // Calculate age from birthdate
  const calculateAge = (birthdate: Date | string | null) => {
    if (!birthdate) return '';

    const birthDate = convertUTCToLocalDate(birthdate);
    if (!birthDate) return '';
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();

    // Adjust age if birthday hasn't occurred yet this year
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return `${age} ${age === 1 ? t(TranslationKeyEnum.AgeSingularYear) : t(TranslationKeyEnum.AgePluralYears)}`;
  };

  // Format adoption date for display
  const formatAdoptionDate = (date: Date | string | null) => {
    const localDate = convertUTCToLocalDate(date);
    if (!localDate) return '';
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const year = localDate.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Format multiple errors for display
  const formatErrors = (validationErrors: ValidationError[]) => {
    if (!validationErrors || validationErrors.length === 0) return null;

    const getTranslatedMessage = (message: string) => {
      const messageKey = message as TranslationKeyEnum;
      if (messageKey && typeof messageKey === 'string') {
        const translatedMessage = t(messageKey);
        // If the translation is the same as the key, it might not be a valid key, so return the original message
        if (translatedMessage !== messageKey) {
          return translatedMessage;
        }
      }
      // If translation fails or message is not a key, return original message
      return message;
    };

    if (validationErrors.length === 1) {
      return getTranslatedMessage(validationErrors[0].message);
    }

    // For multiple errors, show the first one with a count
    const moreCount = validationErrors.length - 1;
    const moreText = moreCount === 1 
      ? t(TranslationKeyEnum.MoreErrorsSingular)
      : t(TranslationKeyEnum.MoreErrorsPlural, String(moreCount));
    return `${getTranslatedMessage(validationErrors[0].message)} (${moreText})`;
  };

  // Render a single pet parent row with all their pets
  const renderPetParentRow = (
    petParent: PetParentInfo,
    pets: PetInfo[],
    petParentIndex: number,
    validationErrors: ValidationError[] = []
  ) => {
    // Check if this pet parent has any validation errors
    const hasErrors = validationErrors && validationErrors.length > 0;

    return (
      <div
        key={`pet-parent-${petParentIndex}`}
        data-pet-parent-index={petParentIndex}
        className="flex flex-row gap-3 justify-between items-start w-full border border-gray-100 rounded-200 p-150"
      >
        {/* Pet Parent Info with Avatar and Error/Success Icon */}
        <div className="flex flex-row items-center gap-100 w-[180px]">
          <div className="flex flex-col items-center gap-25">
            <div
              className={`w-50 h-50 rounded-round flex-shrink-0 ${hasErrors ? 'bg-red-600' : 'bg-avatar-green'}`}
            ></div>
          </div>
          <div className={`flex flex-col gap-25 ${hasErrors ? '' : 'truncate'}`}>
            <span className="text-detail-90 font-medium leading-detail text-gray-1100">
              {`${petParent.firstName} ${petParent.lastName}`}
            </span>
            {hasErrors ? (
              <span className="text-detail-90 font-normal leading-detail text-red-600">
                {formatErrors(validationErrors)}
              </span>
            ) : (
              <span
                className="text-detail-90 font-normal leading-detail text-gray-600 truncate"
                title={petParent.email}
              >
                {petParent.email}
              </span>
            )}
          </div>
        </div>

        {/* Adopter Info Column */}
        <div className="flex flex-col gap-25 flex-1 flex-wrap  w-[90px]">
          <span className="text-detail-90 font-normal leading-detail text-gray-1100">
            {t(TranslationKeyEnum.AdopterInfo)}
          </span>
          <span
            className="text-detail-90 font-normal leading-detail text-black-600"
            title={formatAddress(petParent)}
          >
            {formatAddress(petParent)}
          </span>
          <span className="text-detail-90 font-normal leading-detail text-black-600">
            {petParent.phone}
          </span>
          {isCanadian && (
            <span className="text-detail-90 font-normal leading-detail text-gray-600">
              {petParent.preferredLanguage
                ? petParent.preferredLanguage.charAt(0).toUpperCase() + petParent.preferredLanguage.slice(1).toLowerCase()
                : ''}
         
            </span>
          )}
        </div>

        {/* Pet Info and Adoption Date Columns - unified structure */}
        <div className="flex flex-col gap-25 flex-1">
          {/* Column Headers */}
          <div className="flex flex-row">
            <div className="flex-1 min-w-[120px]">
              <span className="text-detail-90 font-normal leading-detail text-gray-1100">
                {t(TranslationKeyEnum.PetInfo)}
              </span>
            </div>
            <div className="min-w-[120px]">
              <span className="text-detail-90 font-normal leading-detail text-gray-1100">
                {t(TranslationKeyEnum.AdoptionDate)}
              </span>
            </div>
          </div>

          {/* Pet Rows */}
          {pets.map((pet, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <div className="border-t border-gray-100 my-50 w-full"></div>}
              <div className="flex flex-row truncate gap-2">
                <div className="flex flex-col gap-25 flex-1 min-w-[120px]">
                  <span className="text-detail-90 font-normal leading-detail text-black-600 truncate">
                    {formatPetInfo(pet)}
                  </span>
                  {pet.adoptionLocation && (
                    <span className="text-detail-90 font-normal leading-detail text-black-600">
                      {pet.retailPartner ?? pet.adoptionLocation}
                    </span>
                  )}
                </div>
                <div className="min-w-[120px]">
                  <span className="text-detail-90 font-normal leading-detail text-black-600">
                    {formatAdoptionDate(pet.adoptionDate ?? null)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Action Buttons - apply to pet parent as a whole */}
        <div className="flex flex-row gap-1 items-center">
          {/* Always render edit button to maintain alignment, but hide when no errors in spreadsheet flow */}
          <IconButton
            icon={IconName.Edit}
            ariaLabel={t(TranslationKeyEnum.EditPetParent)}
            size={IconButtonSize.Medium}
            variant={IconButtonVariant.Tertiary}
            onClick={() => onEdit(petParentIndex)}
            className={isFromSpreadsheet && !hasErrors ? 'invisible' : ''}
          />
          {onDelete && (
            <IconButton
              icon={IconName.Trash}
              ariaLabel={t(TranslationKeyEnum.DeletePetParent)}
              variant={IconButtonVariant.Tertiary}
              size={IconButtonSize.Medium}
              onClick={() => onDelete(petParentIndex)}
            />
          )}
        </div>
      </div>
    );
  };

  // Handle pet parents based on which data is provided
  const renderPetParents = () => {
    if (parsedData.length === 0) {
      return <Banner variant="warning" title={t(TranslationKeyEnum.NoPetParentsToReview)} />;
    }

    return (
      <>
        <div className="text-lg font-semibold">
          {t(TranslationKeyEnum.PetParents, parsedData.length)}
        </div>
        {parsedData.map((data, index) =>
          renderPetParentRow(data.petParentInfo, data.petInfos, index, data.validationErrors)
        )}
      </>
    );
  };

  const showErrorMessage = () => {
    if (totalErrors === 0) return '';
    if (totalErrors === 1) {
      return t(TranslationKeyEnum.ResolveErrorBeforeSubmitting);
    }
    return t(TranslationKeyEnum.ResolveMultipleErrorsBeforeSubmitting, totalErrors);
  };

  return (
    <Modal
      title={title ?? t(TranslationKeyEnum.ReviewPetParentInformation)}
      eyebrow={eyebrow}
      secondaryEyebrow={secondaryEyebrow ?? reportTimeframe}
      isOpen={isOpen}
      onClose={onClose}
      disableClose={isSubmitting}
      size={size}
      showCloseButton={!isSubmitting && showCloseButton}
      className={className}
      errorMessage={showErrorMessage()}
    >
      <div className="flex flex-col h-full relative">
        <div className="flex-1 overflow-y-auto pb-500">
          <div className="flex flex-col gap-150 w-full">{renderPetParents()}</div>
        </div>
        {/* Fixed Bottom Bar with Submit Button */}
        <div className="sticky bottom-[-32px] left-0 right-0 -mx-8 z-60 bg-white py-200 px-200">
          <div className="flex justify-end gap-150">
            {/* Show Add More button only if not a spreadsheet flow and onAddMore is provided */}
            {!isFromSpreadsheet && onAddMore && (
              <Button
                label={t(TranslationKeyEnum.AddMore)}
                type={ButtonType.Secondary}
                onClick={onAddMore}
                disabled={parsedData?.length === 0}
                className="py-100 px-125 h-[52px]"
              />
            )}
            <Button
              label={
                isSubmitting ? t(TranslationKeyEnum.Submitting) : t(TranslationKeyEnum.SubmitReport)
              }
              icon={isSubmitting ? IconName.Refresh : ''}
              type={ButtonType.Primary}
              onClick={onSubmit}
              disabled={totalErrors > 0 || isSubmitting || parsedData?.length === 0}
              className="py-100 px-125 h-[52px]"
            />
          </div>
        </div>
        ``
      </div>

      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="absolute inset-0 z-100 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[2px] transition-all">
          <div className="flex h-fit flex-col items-center p-200 bg-white rounded-200 shadow-lg border border-gray-100">
            {/* Animated Loader Icon */}
            <div className="animate-spin-reverse mb-150">
              <Icon icon={IconName.Refresh} size={56} className="text-primary" />
            </div>

            <h3 className="font-bold text-heading-150 leading-7 text-black mb-25">
              {t(TranslationKeyEnum.SubmittingReport)}
            </h3>

            <p className="text-detail-90 text-gray-600 text-center">
              {t(TranslationKeyEnum.DoNotCloseModal)}
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ReviewPetParentModal;
